import { Card, Col, Row, Statistic, Tabs, Typography } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { queryKeys } from '@/shared/api/queryKeys'
import { ACTIVE_LIMIT, ActiveSessionsPanel } from '@/features/session/ActiveSessionsPanel'
import { AuthLogTable } from '@/features/session/AuthLogTable'
import { SessionHistoryTable } from '@/features/session/SessionHistoryTable'
import { StaleSessionsPanel } from '@/features/session/StaleSessionsPanel'
import { fetchSessionStats } from '@/features/session/sessionApi'
import { PageHeader } from '@/shared/ui/PageHeader'
import { QueryErrorState } from '@/shared/ui/QueryErrorState'
import { RefreshIndicator } from '@/shared/ui/RefreshIndicator'

/** Ko'rsatkichlar va faol sessiyalar yangilanish davri — BITTA joyda. */

const TABS = ['active', 'history', 'stale', 'auth'] as const
type TabKey = (typeof TABS)[number]

function isTabKey(value: string | null): value is TabKey {
  return value !== null && (TABS as readonly string[]).includes(value)
}

export function SessionsPage() {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()

  /**
   * Faol bo'lim ham URL'da.
   *
   * Jadval holati (sahifa, filtr) allaqachon URL'da edi, bo'lim esa yo'q —
   * natijada tarixning 2-sahifasida turib sahifani yangilasangiz, manzilda
   * `page=2` qolar, lekin ekran "Faol sessiyalar" ga qaytardi. Ya'ni havola
   * ulashilganda ham qabul qiluvchi boshqa joyni ko'rardi.
   */
  const activeTab: TabKey = isTabKey(searchParams.get('tab')) ? (searchParams.get('tab') as TabKey) : 'active'

  const stats = useQuery({
    queryKey: queryKeys.sessions.stats,
    queryFn: fetchSessionStats,
  // ⚠️ Avtoyangilanish OLIB TASHLANDI (egasi qarori 2026-08-20):
  //    u faqat bosh sahifada qoladi. Qolgan ekranlarda u backendga
  //    doimiy yuk berardi, holbuki operator sahifani ochiq qoldirib
  //    boshqa ish bilan band bo'ladi. Yangilash QO'LDA — tugma bor.
  })

  return (
    <>
      <PageHeader
        title={t('nav.sessions')}
        description={t('sessions.intro')}
        extra={
          /*
            Ko'rsatkich `stats` so'roviga tayanadi — u sahifaning YUQORISIDA,
            bo'limdan qat'i nazar doim ekranda turadi va aynan shu davrda
            (15 s) yangilanadi. Faol sessiyalar jadvali ham o'sha davrda
            yangilanadi, lekin uning o'z so'rovi bor — ko'rsatkichni unga
            ulash "Tarix" bo'limida turganda ham "hozirgina yangilandi" deb
            yolg'on aytardi, chunki u bo'limda jadval umuman chizilmaydi.
          */
          <RefreshIndicator
            updatedAt={stats.dataUpdatedAt}
            fetching={stats.isFetching}
          />
        }
      />

      {/*
        ⚠️ KO'RSATKICH YIQILGANDA «—» YETARLI EMAS. Chiziqcha «doiradan
        tashqarida» degan ma'noda ham ishlatiladi, ya'ni yiqilgan so'rov
        ruxsat cheklovi bilan chalkashardi va operator qayta urinmasdi.
      */}
      {stats.isError ? (
        <QueryErrorState
          error={stats.error}
          onRetry={() => void stats.refetch()}
          title={t('sessions.statsFailed')}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12}>
          <Card>
            <Statistic
              title={t('dashboard.activeSessions')}
              value={stats.data?.activeSessions ?? '—'}
            />
            {/*
              ⚠️ KPI VA JADVAL BIR XIL SONNI KO'RSATMAYDI — va bu ekranda
              AYTILADI. Ko'rsatkich butun tenant bo'yicha
              (`GET /api/sessions/stats`, parametrsiz), pastdagi jadval esa
              `limit=200`: 340 ta sessiyada tepada «340», pastda 200 qator
              turardi va farq faqat jadval sarlavhasidagi mayda yozuvda
              tilga olinardi.
            */}
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {(stats.data?.activeSessions ?? 0) > ACTIVE_LIMIT
                ? t('sessions.kpiOverLimit', { limit: ACTIVE_LIMIT })
                : t('sessions.kpiScopeNote')}
            </Typography.Text>
          </Card>
        </Col>
        <Col xs={24} sm={12}>
          <Card>
            {/*
              Ikki son ATAYLAB alohida: ko'p qurilmali abonent sessiyalar
              sonida bir necha marta, abonentlar sonida bir marta sanaladi.
              Ularni bitta ko'rsatkichga birlashtirish "online abonentlar"
              sonini oshirib ko'rsatardi.
            */}
            <Statistic
              title={t('dashboard.onlineSubscribers')}
              value={stats.data?.onlineSubscribers ?? '—'}
            />
          </Card>
        </Col>
      </Row>

      <Tabs
        activeKey={activeTab}
        onChange={(key) =>
          setSearchParams(
            (current) => {
              const next = new URLSearchParams(current)
              // Standart bo'limda parametr yozilmaydi — toza manzil
              // `/sessions` bo'lib qolsin, `?tab=active` kabi shovqinsiz
              if (key === 'active') next.delete('tab')
              else next.set('tab', key)
              return next
            },
            // Bo'lim almashishi tarixga yangi yozuv qo'shmasin: "orqaga"
            // tugmasi sessiyalar sahifasidan CHIQISHI kutiladi
            { replace: true },
          )
        }
        items={[
          {
            key: 'active',
            label: t('sessions.tabActive'),
            /*
              ⚠️ FAQAT O'Z BO'LIMI OCHIQ BO'LGANDA CHIZILADI.

              antd `Tabs` bo'limni BIR MARTA ochilgandan keyin MOUNT'da
              qoldiradi (`display: none` bilan yashiradi, `destroyInactiveTabPane`
              yoqilmagan). Ya'ni operator "Faol"ga bir marta kirib, keyin
              "Tarix"da soatlab ishlasa ham, bu panel har 15 soniyada
              `GET /api/sessions/active?limit=200` ni yuboraverardi — ekranda
              KO'RINMAYDIGAN, `radacct` ustidagi og'ir so'rov.

              Boshqa bo'limlar (`Tarix`, `Osilgan`, `Auth`) avtomatik
              yangilanmaydi, shuning uchun ularga bu shart kerak emas —
              ular mount'da qolsa ham so'rov yubormaydi va jadval holati
              (sahifa, qidiruv) saqlanib qoladi.
            */
            children: activeTab === 'active' ? <ActiveSessionsPanel /> : null,
          },
          {
            key: 'history',
            label: t('sessions.tabHistory'),
            children: (
              <Card>
                <SessionHistoryTable />
              </Card>
            ),
          },
          { key: 'stale', label: t('sessions.tabStale'), children: <StaleSessionsPanel /> },
          {
            key: 'auth',
            label: t('sessions.tabAuth'),
            children: (
              <Card>
                {/* URL prefiksi: tarix jadvali bilan bir sahifada va ikkalasi
                    ham `page`/`q` ishlatadi — prefikssiz bittasining sahifasi
                    ikkinchisiniki bilan almashib ketardi */}
                <AuthLogTable urlPrefix="auth" />
              </Card>
            ),
          },
        ]}
      />
    </>
  )
}
